<script setup lang="ts">
import { UTable } from '#components'
import type { TableColumn } from '#ui/types'

const props = defineProps<{
	rows?: any[]
	columns?: TableColumn[]
	loading?: boolean
	sticky?: boolean
	sortField?: string
	sortDirection?: 'asc' | 'desc'
}>()

const emit = defineEmits<{
	sort: [sortBy: { column: string; direction: 'asc' | 'desc' }]
}>()

const slots = useSlots()

const { width } = useWindowSize()
const isMobile = computed(() => width.value < 768)
const getNestedValue = (obj: any, path: string): any => {
	if (!obj || !path) return obj

	const keys = path.split('.')
	let result = obj

	for (const key of keys) {
		if (result === null || result === undefined) return result
		result = result[key]
	}

	return result
}

// Модифицированные колонки для отключения встроенной сортировки UTable
const modifiedColumns = computed(() => {
	if (!props.columns) return []
	return props.columns.map(column => ({
		...column,
		sortable: false // Отключаем встроенную сортировку UTable
	}))
})

const handleSort = (column: TableColumn) => {
   if (!column.sortable) return false
	let direction: 'asc' | 'desc' = 'asc'
	if (props.sortField === column.key) {
		direction = props.sortDirection === 'asc' ? 'desc' : 'asc'
	}

	emit('sort', { column: column.key, direction })
}
</script>

<template>
	<div>
		<UTable
			v-if="!isMobile"
			:rows="rows"
			:columns="modifiedColumns"
			:loading="loading"
			:sticky="sticky"
			:sort="sortField && sortDirection ? {
				column: sortField,
				direction: sortDirection
			} : undefined"
		>
			<template v-for="column in props.columns" #[`${column.key}-header`]>
				<button
					@click="handleSort(column)"
					class="flex items-center gap-2 px-2 py-1 rounded transition-colors min-w-0"
					:class="{
						'text-primary': sortField === column.key,
						'text-gray-700 dark:text-gray-300': sortField !== column.key,
                  'hover:bg-gray-100 dark:hover:bg-gray-800': !!column.sortable,
                  'cursor-default': !column.sortable
					}"
				>
					<span class="break-words">{{ column.label || column.key }}</span>
					<UIcon
						v-if="sortField === column.key"
						:name="sortDirection === 'asc' ? 'material-symbols:arrow-upward' : 'material-symbols:arrow-downward'"
						class="w-4 h-4 flex-shrink-0"
					/>
				</button>
			</template>
			<template v-for="(_, slot) in slots" #[slot]="scope">
				<component :is="slots[slot]" v-bind="scope" v-if="slots[slot]" />
			</template>
		</UTable>

		<div v-if="isMobile" class="space-y-4 mt-2">
			<UCard
				v-for="(row, index) in rows"
				:key="index"
				:class="[
					index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-gray-800/50',
					loading ? 'loading-pulse' : ''
				]"
			>
				<div
					v-for="(column, colIndex) in columns"
					:key="colIndex"
					class="flex flex-col py-1"
				>
					<div class="font-semibold text-gray-700 dark:text-gray-300 mb-1">
						{{ column.label || column.key }}
					</div>

					<div
						v-if="slots[`${column.key}-data`]"
						class="border-b border-gray-200 dark:border-gray-700 pb-2"
					>
						<component :is="slots[`${column.key}-data`]" :row="row" v-if="slots[`${column.key}-data`]" />
					</div>

					<span v-else class="text-gray-600 dark:text-gray-400">
						{{ getNestedValue(row, column.key) }}
					</span>
				</div>
			</UCard>
		</div>

	</div>
</template>

<style scoped>
.loading-pulse {
	animation: pulse 1.5s infinite;
}
@keyframes pulse {
	0% {
		opacity: 1;
	}
	50% {
		opacity: 0.5;
	}
	100% {
		opacity: 1;
	}
}
</style>